import { InfoOutlineIcon, SearchIcon } from '@chakra-ui/icons';
import { Box, Divider, Flex } from '@chakra-ui/layout';
import {
  Button,
  ButtonGroup,
  Checkbox,
  CheckboxGroup,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

import { RevelioTooltip } from '@revelio/ui';

import styles from '../../Modal/themedModal.module.css';

interface ColumnSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  columns: { value?: string; label?: string; tooltip?: string }[];
  setSavedCheckedColumns: (value: string[]) => void;
  savedCheckedColumns: string[];
  initialCheckedColumn?: string[];
  resetCheckedColumns?: string[];
  onSave?: (checkedColumns: string[]) => void;
}

export const ColumnSelectModal = ({
  isOpen,
  onClose,
  columns,
  setSavedCheckedColumns,
  savedCheckedColumns,
  initialCheckedColumn,
  resetCheckedColumns,
  onSave,
}: ColumnSelectModalProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [checkedColumns, setCheckedColumns] = useState<string[]>([]);
  const [searchedColumns, setSearchedColumns] = useState(columns);
  const [columnHeaders, setColumnHeaders] = useState<string[]>([]);

  useEffect(() => {
    const searchedCols = columns.filter((column) =>
      column?.value?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    setSearchedColumns(searchedCols);
  }, [columns, searchTerm]);

  const newColumnHeaders = useMemo(
    () => columns.map((col) => col?.value || ''),
    [columns]
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    if (!isEqual(columnHeaders, newColumnHeaders)) {
      setColumnHeaders(newColumnHeaders);

      // Apply defaults when we have initialCheckedColumn
      if (initialCheckedColumn && newColumnHeaders.length > 0) {
        const defaultCheckedColumns = newColumnHeaders.filter((col) =>
          initialCheckedColumn?.includes(col)
        );
        if (defaultCheckedColumns?.length > 0) {
          setCheckedColumns(defaultCheckedColumns);
          setSavedCheckedColumns(defaultCheckedColumns);
        }
      }
    }
  }, [
    isOpen,
    newColumnHeaders,
    columnHeaders,
    setSavedCheckedColumns,
    initialCheckedColumn,
  ]);

  useEffect(() => {
    if (isOpen) {
      setCheckedColumns(savedCheckedColumns);
    }

    setSearchTerm('');
  }, [isOpen, savedCheckedColumns]);

  const onChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const onChangeColumns = (values: string[]) => {
    const sortedValues = columns
      .filter((col): col is { value: string; label?: string } => {
        return typeof col.value === 'string' && values.includes(col.value);
      })
      .map((col) => col.value);
    setCheckedColumns(sortedValues);
  };

  const onSaveColumn = () => {
    setSavedCheckedColumns(checkedColumns);
    if (onSave) {
      onSave(checkedColumns);
    }
    onClose();
  };

  const columnsToShow = searchedColumns.map((column) => ({
    value: column?.value,
    label: column?.label,
    tooltip: column?.tooltip,
  }));
  const resetColumns = useMemo(() => {
    const resetSource = resetCheckedColumns ?? initialCheckedColumn;
    if (!resetSource || resetSource.length === 0) {
      return [];
    }

    return columns
      .filter((col): col is { value: string; label?: string } => {
        return typeof col.value === 'string' && resetSource.includes(col.value);
      })
      .map((col) => col.value);
  }, [columns, initialCheckedColumn, resetCheckedColumns]);

  const onResetSelections = () => {
    setCheckedColumns(resetColumns);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      returnFocusOnClose={false}
      size="lg"
    >
      <ModalOverlay />
      <ModalContent className={styles.content}>
        <ModalHeader color="text.primary">Manage Columns</ModalHeader>
        <ModalCloseButton />
        <Divider orientation="horizontal" />
        <ModalBody>
          <InputGroup>
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={onChangeSearch}
            />
            <InputRightElement>
              <SearchIcon w={3.5} h={3.5} className={styles.muted} />
            </InputRightElement>
          </InputGroup>
          <Box
            className={styles.scrollArea}
            overflowY="scroll"
            maxH="350px"
            mt={2}
            pl={1}
          >
            <CheckboxGroup
              colorScheme="green"
              value={checkedColumns}
              onChange={onChangeColumns}
            >
              <Flex flexDirection="column">
                {columnsToShow
                  .filter((column) => column.label)
                  .map((column) => {
                    return (
                      <Checkbox
                        key={column.value}
                        value={column.value}
                        color="text.primary"
                        data-testid={column.value + '-checkbox'}
                        mb={2}
                      >
                        <Flex alignItems="center" gap={1}>
                          {column.label}
                          {column.tooltip && (
                            <RevelioTooltip
                              label={column.tooltip}
                              placement="top"
                              openDelay={200}
                            >
                              <InfoOutlineIcon
                                boxSize={3}
                                className={styles.muted}
                                tabIndex={0}
                                data-testid={`${column.value}-tooltip`}
                              />
                            </RevelioTooltip>
                          )}
                        </Flex>
                      </Checkbox>
                    );
                  })}
              </Flex>
            </CheckboxGroup>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Flex justifyContent="space-between" alignItems="center" width="100%">
            <Flex>
              {(resetCheckedColumns || initialCheckedColumn) && (
                <Button
                  variant="ghost"
                  size="sm"
                  fontSize="xs"
                  colorScheme="red"
                  onClick={onResetSelections}
                  data-testid="reset-table-columns-button"
                >
                  Reset
                </Button>
              )}
            </Flex>
            <ButtonGroup spacing={4}>
              <Button
                variant="link"
                fontSize="12px"
                colorScheme="gray"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                borderRadius="4px"
                fontSize="12px"
                fontWeight="600"
                colorScheme="green"
                variant="solid"
                size="sm"
                onClick={onSaveColumn}
                data-testid="save-table-columns-button"
              >
                Save
              </Button>
            </ButtonGroup>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
